import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
	type LWApiPublicSupportPhoneNumbersResultRawI,
	type LWApiPublicSupportPhoneNumbersParamsRawI,
} from '@liquidweb-dev/club-sauce/public/support/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import api from 'modules/queries/api';

interface SupportTier {
	description: string;
	display: string;
	name: string;
}

interface SupportTierInfo {
	tier: SupportTier;
	hasSupport: boolean;
}

type SupportPhoneNumbers = Omit<
	LWApiPublicSupportPhoneNumbersResultRawI,
	'support_tier'
> & { supportTier: SupportTierInfo };

const normalizeSupportTier = (
	data: LWApiPublicSupportPhoneNumbersResultRawI,
): SupportTierInfo => {
	return {
		tier: {
			description: data.support_tier?.description || '',
			display: data.support_tier?.display_name || '',
			name: data.support_tier?.tier_name || '',
		},
		hasSupport: data?.support_tier?.tier_name !== 'Basic',
	};
};

const normalize = (
	data: LWApiPublicSupportPhoneNumbersResultRawI,
): SupportPhoneNumbers => {
	const { support_tier: _supportTier, ...rest } = data;
	return {
		...rest,
		supportTier: normalizeSupportTier(data),
	};
};
export const SUPPORT_PHONE_NUMBERS_QUERY_KEY = '/support/phoneNumbers';

const useSupportPhoneNumbers = (
	params: LWApiPublicSupportPhoneNumbersParamsRawI,
	options?: Omit<
		UseQueryOptions<
			LWApiPublicSupportPhoneNumbersResultRawI,
			ApiErrorI,
			SupportPhoneNumbers
		>,
		'select'
	>,
) =>
	useQuery({
		queryKey: [SUPPORT_PHONE_NUMBERS_QUERY_KEY, params],
		queryFn: () => api.support.apiRaw.phoneNumbers(params),
		select: normalize,
		...options,
	});

export default useSupportPhoneNumbers;
